import {
  IonContent,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { arrowBackOutline } from "ionicons/icons";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Header from "src/components/Header";
import { campoEstilo, Etiqueta, fmtFecha } from "src/components/SolicitudCapacitacionCampos";
import SolicitudSstFormulario from "src/components/SolicitudSstFormulario";
import {
  cargarPermisoSst,
  ESTADOS_SOLICITUD_SST,
  permisoSstEnSesion,
  SolicitudSstAPI,
  SolicitudSstResumen,
  TipoSolicitudSst,
} from "src/services/sst";

type Tab = "nueva" | "mis";

export const EstadoSolicitudSstChip: React.FC<{ estado: string }> = ({ estado }) => {
  const e = ESTADOS_SOLICITUD_SST[estado] || { etiqueta: estado, color: "#999" };
  return (
    <span
      style={{
        background: e.color,
        color: "#fff",
        borderRadius: 10,
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {e.etiqueta}
    </span>
  );
};

/**
 * Solicitudes a SST (/sst/solicitudes): se entra por Solicitudes > SST.
 * Pestaña Nueva (formulario según el tipo, declarado por EMSYS) y Mis
 * solicitudes. SST las gestiona en EMSYS; el detalle permite corregir una
 * devuelta o cancelar.
 */
const SolicitudesSst: React.FC = () => {
  const [present] = useIonToast();
  const history = useHistory();
  const location = useLocation();

  const [tab, setTab] = useState<Tab>(
    new URLSearchParams(location.search).get("tab") === "mis" ? "mis" : "nueva"
  );
  const [tipos, setTipos] = useState<TipoSolicitudSst[]>([]);
  const [tipo, setTipo] = useState<TipoSolicitudSst | null>(null);
  const [mis, setMis] = useState<SolicitudSstResumen[]>([]);
  const [cargando, setCargando] = useState(true);
  // null = consultando a EMSYS; false = no es fiscal, jefe de operaciones ni administrativo.
  const [puede, setPuede] = useState<boolean | null>(permisoSstEnSesion());

  const avisar = useCallback(
    (message: string, color = "warning") => present({ message, duration: 3500, color }),
    [present]
  );

  useEffect(() => {
    cargarPermisoSst().then((p) => {
      setPuede(p === null ? true : p); // si no se pudo consultar, decide EMSYS al enviar
      if (p === false) setTab("mis");
    });
  }, []);

  useEffect(() => {
    SolicitudSstAPI.catalogo()
      .then((r) => setTipos(r.data.data.tipos || []))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudo cargar el formulario", "danger"));
  }, [avisar]);

  const cargarMis = useCallback(() => {
    setCargando(true);
    SolicitudSstAPI.mis()
      .then((r) => setMis(r.data.data || []))
      .catch((err) => avisar(err?.response?.data?.error || "No se pudieron obtener las solicitudes", "danger"))
      .finally(() => setCargando(false));
  }, [avisar]);

  // Las rutas van en un <Switch>: cada entrada monta la página de nuevo.
  useEffect(() => {
    if (tab === "mis") cargarMis();
  }, [tab, cargarMis]);

  const enviar = async (payload: Record<string, any>) => {
    try {
      const r = await SolicitudSstAPI.crear(payload);
      avisar(r.data.data?.info || "Solicitud enviada a SST", "success");
      setTipo(null);
      setTab("mis");
      return true; // el formulario se desmonta: no hay que volver a habilitarlo
    } catch (err: any) {
      avisar(err?.response?.data?.error || "No se pudo enviar la solicitud", "danger");
      return false;
    }
  };

  return (
    <IonPage>
      <Header titulo="Solicitudes a SST" botonIzq={{ icono: arrowBackOutline, url: "/solicitudes" }} />
      <IonContent id="sst-solicitudes">
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} style={{ marginBottom: 16 }}>
            <IonSegmentButton value="nueva">
              <IonLabel>Nueva</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="mis">
              <IonLabel>Mis solicitudes</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {tab === "nueva" && puede === false && (
            <p style={{ color: "#555", fontSize: 14 }}>
              Solo fiscales, jefes de operaciones y personal administrativo pueden cargar solicitudes a SST.
            </p>
          )}

          {tab === "nueva" && puede === null && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          )}

          {tab === "nueva" && puede === true && (
            <>
              <div style={campoEstilo}>
                <Etiqueta texto="Solicitud" obligatorio />
                {/* action-sheet como el resto de la app: con interface="popover" el
                    contenido queda con alto 0 y no se ven las opciones. */}
                <IonSelect
                  interface="action-sheet"
                  interfaceOptions={{ header: "Tipo de solicitud" }}
                  cancelText="Cancelar"
                  placeholder="Seleccionar el tipo de solicitud"
                  value={tipo?.codigo}
                  onIonChange={(e) => setTipo(tipos.find((t) => t.codigo === e.detail.value) || null)}
                  style={{ border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 }}
                >
                  {tipos.map((t) => (
                    <IonSelectOption key={t.codigo} value={t.codigo}>
                      {t.etiqueta}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </div>

              {tipo && (
                <>
                  <h6 style={{ fontWeight: 700, color: "#444", textTransform: "uppercase", margin: "8px 0 16px" }}>
                    {tipo.etiqueta}
                  </h6>
                  <SolicitudSstFormulario
                    tipo={tipo}
                    textoBoton="Enviar a SST"
                    onEnviar={enviar}
                    onError={(m) => avisar(m)}
                  />
                </>
              )}
            </>
          )}

          {tab === "mis" &&
            (cargando ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <IonSpinner />
              </div>
            ) : !mis.length ? (
              <p style={{ color: "#777" }}>Todavía no enviaste solicitudes a SST.</p>
            ) : (
              mis.map((s) => (
                <div
                  key={s.id}
                  onClick={() => history.push(`/sst/solicitudes/${s.id}`)}
                  style={{
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    padding: "10px 12px",
                    marginBottom: 8,
                    borderLeft: `5px solid ${(ESTADOS_SOLICITUD_SST[s.estado] || { color: "#999" }).color}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                    <strong style={{ fontSize: 14 }}>N° {s.id}</strong>
                    <EstadoSolicitudSstChip estado={s.estado} />
                  </div>
                  <div style={{ fontSize: 13, color: "#444", marginTop: 4 }}>{s.tipo_label}</div>
                  <div style={{ fontSize: 12, color: "#777", marginTop: 2 }}>
                    {s.ps_nombre ? `${s.ps_nombre} · ` : ""}
                    {fmtFecha(s.fecha)} {s.hora || ""}
                    {s.estado === "DEVUELTA" && <strong style={{ color: "#8e44ad" }}> · Tocá para corregir</strong>}
                  </div>
                </div>
              ))
            ))}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudesSst;
